import { useState, useEffect } from "react";
import client from "@/api";
import { useApp } from "@/context/AppContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { PasswordInput } from "@/components/PasswordInput";
import { LogIn, UserPlus, ArrowLeft, MailCheck } from "lucide-react";

function fmtErr(detail, fallback) {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ") || fallback;
  if (detail && typeof detail.msg === "string") return detail.msg;
  return fallback;
}

export default function Auth() {
  const { t, user, accessStatus, setUser, setAccessStatus } = useApp();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (user) navigate(accessStatus === "approved" ? "/dashboard" : "/status");
  }, [user, accessStatus, navigate]);

  const loginGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const afterAuth = (data) => {
    setUser(data.user);
    setAccessStatus(data.access_status);
    navigate(data.access_status === "approved" ? "/dashboard" : "/status");
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await client.post("/auth/login", { email: email.trim(), password });
      afterAuth(data);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail, t("error")));
    } finally { setBusy(false); }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error(t("pw_mismatch")); return; }
    setBusy(true);
    try {
      const { data } = await client.post("/auth/register", { name: name.trim(), email: email.trim(), password });
      afterAuth(data);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail, t("error")));
    } finally { setBusy(false); }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await client.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail, t("error")));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] bg-grain">
      <Header variant="public" />
      <div className="flex items-center justify-center px-4 py-12">
        <div className="card-dark rounded-2xl p-8 w-full max-w-md fade-up" data-testid="auth-card">
          {mode !== "forgot" && (
            <div className="flex gap-2 mb-6 p-1 rounded-xl bg-[#141414] border border-white/5">
              <button data-testid="auth-tab-login" onClick={() => setMode("login")}
                className={`focusable flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors ${mode === "login" ? "bg-[#D21F2A] text-white" : "text-[#C9CDD3] hover:text-white"}`}>
                {t("auth_tab_login")}
              </button>
              <button data-testid="auth-tab-register" onClick={() => setMode("register")}
                className={`focusable flex-1 py-2.5 rounded-lg text-sm font-semibold transition-colors ${mode === "register" ? "bg-[#D21F2A] text-white" : "text-[#C9CDD3] hover:text-white"}`}>
                {t("auth_tab_register")}
              </button>
            </div>
          )}

          {mode === "login" && (
            <form onSubmit={submitLogin} data-testid="login-form">
              <h1 className="font-serif text-2xl text-white mb-6">{t("auth_login_title")}</h1>
              <label className="block text-xs uppercase tracking-wider text-[#C9CDD3] mb-1">{t("email")}</label>
              <input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
                className="focusable w-full mb-4 px-4 py-3 rounded-lg bg-[#141414] border border-[#D21F2A]/20 text-white" />
              <label className="block text-xs uppercase tracking-wider text-[#C9CDD3] mb-1">{t("password")}</label>
              <div className="mb-2"><PasswordInput testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" /></div>
              <button type="button" data-testid="forgot-link" onClick={() => { setMode("forgot"); setSent(false); }}
                className="focusable text-sm text-[#D21F2A] hover:underline mb-6 block">{t("forgot_pw")}</button>
              <button data-testid="login-submit" disabled={busy} type="submit"
                className="btn-gold focusable w-full py-3 rounded-lg flex items-center justify-center gap-2">
                <LogIn className="w-5 h-5" /> {busy ? t("loading") : t("login")}
              </button>
            </form>
          )}

          {mode === "register" && (
            <form onSubmit={submitRegister} data-testid="register-form">
              <h1 className="font-serif text-2xl text-white mb-2">{t("auth_register_title")}</h1>
              <p className="text-sm text-[#9AA0A6] mb-6">{t("direct_access_note")}</p>
              <label className="block text-xs uppercase tracking-wider text-[#C9CDD3] mb-1">{t("name_label")}</label>
              <input data-testid="register-name-input" type="text" value={name} onChange={(e) => setName(e.target.value)} required autoComplete="name"
                className="focusable w-full mb-4 px-4 py-3 rounded-lg bg-[#141414] border border-[#D21F2A]/20 text-white" />
              <label className="block text-xs uppercase tracking-wider text-[#C9CDD3] mb-1">{t("email")}</label>
              <input data-testid="register-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
                className="focusable w-full mb-4 px-4 py-3 rounded-lg bg-[#141414] border border-[#D21F2A]/20 text-white" />
              <label className="block text-xs uppercase tracking-wider text-[#C9CDD3] mb-1">{t("password")}</label>
              <div className="mb-1"><PasswordInput testid="register-password-input" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" /></div>
              <p className="text-xs text-[#9AA0A6] mb-4">{t("min_pw_hint")}</p>
              <label className="block text-xs uppercase tracking-wider text-[#C9CDD3] mb-1">{t("pw_confirm")}</label>
              <div className="mb-6"><PasswordInput testid="register-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" /></div>
              <button data-testid="register-submit" disabled={busy} type="submit"
                className="btn-gold focusable w-full py-3 rounded-lg flex items-center justify-center gap-2">
                <UserPlus className="w-5 h-5" /> {busy ? t("loading") : t("register_cta")}
              </button>
            </form>
          )}

          {mode === "forgot" && (
            sent ? (
              <div data-testid="forgot-sent" className="text-center py-4">
                <MailCheck className="w-12 h-12 text-[#D21F2A] mx-auto mb-4" />
                <h1 className="font-serif text-xl text-white mb-3">{t("forgot_title")}</h1>
                <p className="text-sm text-[#C9CDD3] mb-6">{t("forgot_sent")}</p>
                <button data-testid="forgot-back-sent" onClick={() => { setMode("login"); setSent(false); }}
                  className="focusable text-sm text-[#D21F2A] hover:underline inline-flex items-center gap-1">
                  <ArrowLeft className="w-4 h-4" /> {t("back_to_login")}
                </button>
              </div>
            ) : (
              <form onSubmit={submitForgot} data-testid="forgot-form">
                <h1 className="font-serif text-xl text-white mb-2">{t("forgot_title")}</h1>
                <p className="text-sm text-[#9AA0A6] mb-6">{t("forgot_desc")}</p>
                <label className="block text-xs uppercase tracking-wider text-[#C9CDD3] mb-1">{t("email")}</label>
                <input data-testid="forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="email"
                  className="focusable w-full mb-6 px-4 py-3 rounded-lg bg-[#141414] border border-[#D21F2A]/20 text-white" />
                <button data-testid="forgot-submit" disabled={busy} type="submit"
                  className="btn-gold focusable w-full py-3 rounded-lg">{busy ? t("loading") : t("forgot_submit")}</button>
                <button type="button" data-testid="forgot-back" onClick={() => setMode("login")}
                  className="focusable text-sm text-[#D21F2A] hover:underline mt-4 inline-flex items-center gap-1">
                  <ArrowLeft className="w-4 h-4" /> {t("back_to_login")}
                </button>
              </form>
            )
          )}

          {mode !== "forgot" && (
            <>
              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-xs text-[#9AA0A6] uppercase tracking-wider">{t("or")}</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <button data-testid="auth-google-btn" onClick={loginGoogle}
                className="focusable w-full py-3 rounded-lg border border-white/15 text-white hover:border-[#D21F2A] transition-colors flex items-center justify-center gap-2">
                <LogIn className="w-5 h-5" /> {t("cta_login_google")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
